import React, { useState, useEffect } from 'react';
import { AlignLeft, AlignCenter, AlignRight, AlignJustify } from 'lucide-react';
import { ALL_FONTS, GOOGLE_FONTS, loadGoogleFont } from '../../../data/googleFonts';
import { useEditor } from '../../../contexts/EditorContext';

const MASK_SHAPES = [
  { id: 'rect',     label: 'Rectangle',
    svg: <svg viewBox="0 0 40 40" fill="none"><rect x="2" y="2" width="36" height="36" fill="#94a3b8"/></svg> },
  { id: 'circle',   label: 'Circle',
    svg: <svg viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="18" fill="#94a3b8"/></svg> },
  { id: 'triangle', label: 'Triangle',
    svg: <svg viewBox="0 0 40 40" fill="none"><polygon points="20,2 38,36 2,36" fill="#94a3b8"/></svg> },
  { id: 'star',     label: 'Star',
    svg: <svg viewBox="0 0 40 40" fill="none"><polygon points="20,2 24,14 38,14 27,22 31,36 20,28 9,36 13,22 2,14 16,14" fill="#94a3b8"/></svg> },
  { id: 'diamond',  label: 'Diamond',
    svg: <svg viewBox="0 0 40 40" fill="none"><polygon points="20,2 38,20 20,38 2,20" fill="#94a3b8"/></svg> },
  { id: 'pentagon', label: 'Pentagon',
    svg: <svg viewBox="0 0 40 40" fill="none"><polygon points="20,2 38,14 32,36 8,36 2,14" fill="#94a3b8"/></svg> },
  { id: 'hexagon',  label: 'Hexagon',
    svg: <svg viewBox="0 0 40 40" fill="none"><polygon points="20,2 36,11 36,29 20,38 4,29 4,11" fill="#94a3b8"/></svg> },
  { id: 'octagon',  label: 'Octagon',
    svg: <svg viewBox="0 0 40 40" fill="none"><polygon points="13,2 27,2 38,13 38,27 27,38 13,38 2,27 2,13" fill="#94a3b8"/></svg> },
  { id: 'cross',    label: 'Cross',
    svg: <svg viewBox="0 0 40 40" fill="none"><rect x="14" y="2" width="12" height="36" rx="2" fill="#94a3b8"/><rect x="2" y="14" width="36" height="12" rx="2" fill="#94a3b8"/></svg> },
  { id: 'heart',    label: 'Heart',
    svg: <svg viewBox="0 0 40 40" fill="none"><path d="M20 34 C4 24 2 10 2 10 C2 4 8 2 13 2 C17 2 19 5 20 8 C21 5 23 2 27 2 C32 2 38 4 38 10 C38 10 36 24 20 34Z" fill="#94a3b8"/></svg> },
  { id: 'cloud',    label: 'Cloud',
    svg: <svg viewBox="0 0 40 30" fill="none"><path d="M7 24 Q2 24 2 18 Q2 12 8 12 Q8 4 16 4 Q22 4 23 10 Q27 8 30 12 Q35 12 35 18 Q35 24 30 24Z" fill="#94a3b8"/></svg> },
  { id: 'rounded-rect',  label: 'Round Rect',
    svg: <svg viewBox="0 0 40 40" fill="none"><rect x="2" y="2" width="36" height="36" rx="7" fill="#94a3b8"/></svg> },
  { id: 'ellipse',       label: 'Ellipse',
    svg: <svg viewBox="0 0 50 32" fill="none"><ellipse cx="25" cy="16" rx="23" ry="13" fill="#94a3b8"/></svg> },
  { id: 'arrow-right',   label: 'Arrow',
    svg: <svg viewBox="0 0 44 32" fill="none"><polygon points="0,9 28,9 28,0 44,16 28,32 28,23 0,23" fill="#94a3b8"/></svg> },
  { id: 'star6',         label: '6-Pt Star',
    svg: <svg viewBox="0 0 40 40" fill="none"><polygon points="20,2 25,12 36,11 29,20 36,29 25,28 20,38 15,28 4,29 11,20 4,11 15,12" fill="#94a3b8"/></svg> },
  { id: 'speech-bubble', label: 'Callout',
    svg: <svg viewBox="0 0 40 44" fill="none"><path d="M4 0 H36 Q40 0 40 4 V30 Q40 34 36 34 H18 L8 44 V34 H4 Q0 34 0 30 V4 Q0 0 4 0Z" fill="#94a3b8"/></svg> },
  { id: 'trapezoid',     label: 'Trapezoid',
    svg: <svg viewBox="0 0 44 32" fill="none"><polygon points="8,0 36,0 44,32 0,32" fill="#94a3b8"/></svg> },
  { id: 'shield',        label: 'Shield',
    svg: <svg viewBox="0 0 40 46" fill="none"><path d="M5 0 H35 Q40 0 40 5 V24 Q40 38 20 46 Q0 38 0 24 V5 Q0 0 5 0Z" fill="#94a3b8"/></svg> },
  { id: 'chevron',       label: 'Chevron',
    svg: <svg viewBox="0 0 44 32" fill="none"><polygon points="0,0 32,0 44,16 32,32 0,32 12,16" fill="#94a3b8"/></svg> },
];

const PropertiesPanel = () => {
  const {
    slides,
    activeSlide,
    selectedElements,
    updateElement,
    dimensions,
    setSlideBackground,
  } = useEditor();
  
  const currentSlide = slides[activeSlide];
  const elementId = selectedElements[0] ?? null;
  const element = currentSlide?.elements.find(el => el.id === elementId) ?? null;

  const [fontSearch, setFontSearch] = useState('');
  const [showFontList, setShowFontList] = useState(false);

  useEffect(() => {
    if (element?.fontFamily) loadGoogleFont(element.fontFamily);
  }, [element?.fontFamily]);

  useEffect(() => {
    if (showFontList) GOOGLE_FONTS.forEach(f => loadGoogleFont(f.name));
  }, [showFontList]);

  if (selectedElements.length === 0) {
    const bgColor = currentSlide?.backgroundColor || '#FFFFFF';
    return (
      <div className="w-64 lg:w-72 xl:w-80 bg-white border-l border-gray-200 flex flex-col overflow-y-auto">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700">Canvas Background</h3>
          <p className="text-xs text-gray-400 mt-0.5">Slide {(slides.indexOf(currentSlide) + 1) || 1} background color</p>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">Background Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={bgColor}
                onChange={(e) => setSlideBackground(e.target.value)}
                className="w-10 h-10 rounded cursor-pointer border border-gray-300 p-0.5"
              />
              <input
                type="text"
                value={bgColor}
                onChange={(e) => setSlideBackground(e.target.value)}
                className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm font-mono uppercase"
                maxLength={7}
              />
            </div>
          </div>
          <div className="grid grid-cols-6 gap-1.5 pt-1">
            {['#FFFFFF','#000000','#1E40AF','#10B981','#EF4444','#F59E0B',
              '#7C3AED','#EC4899','#F3F4F6','#111827','#DBEAFE','#D1FAE5'].map(color => (
              <button
                key={color}
                onClick={() => setSlideBackground(color)}
                className={`w-8 h-8 rounded border-2 transition-transform hover:scale-110 ${
                  bgColor.toLowerCase() === color.toLowerCase()
                    ? 'border-blue-500 scale-110'
                    : 'border-transparent hover:border-gray-300'
                }`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
          <p className="text-xs text-gray-400 pt-2">Select an element to edit its properties.</p>
        </div>
      </div>
    );
  }
  
  if (selectedElements.length > 1) {
    return (
      <div className="w-80 bg-white border-l border-gray-200 p-4">
        <h3 className="text-lg font-semibold mb-4">Properties</h3>
        <p className="text-sm text-gray-500">{selectedElements.length} elements selected</p>
        <p className="text-xs text-gray-400 mt-2">Multi-edit coming soon...</p>
      </div>
    );
  }
  
  if (!element) return null;
  
  const handleChange = (property, value) => {
    updateElement(elementId, { [property]: value });
  };

  // Text style helpers
  const isBold = (element.fontStyle || '').includes('bold');
  const isItalic = (element.fontStyle || '').includes('italic');
  const isUnderline = element.textDecoration === 'underline';

  const toggleBold = () => {
    const next = !isBold;
    handleChange('fontStyle', next && isItalic ? 'bold italic' : next ? 'bold' : isItalic ? 'italic' : 'normal');
  };
  const toggleItalic = () => {
    const next = !isItalic;
    handleChange('fontStyle', next && isBold ? 'bold italic' : next ? 'italic' : isBold ? 'bold' : 'normal');
  };
  const toggleUnderline = () => handleChange('textDecoration', isUnderline ? '' : 'underline');

  // Align-to-canvas helpers
  const RADIAL_TYPES = ['circle', 'triangle', 'diamond', 'pentagon', 'hexagon', 'octagon', 'crescent'];
  const DUAL_RADIUS_TYPES = ['ring', 'star'];
  const CENTERED_TYPES = [...RADIAL_TYPES, ...DUAL_RADIUS_TYPES, 'ellipse'];

  const getElementWidth = () => {
    if (RADIAL_TYPES.includes(element.type)) return (element.radius || 80) * 2;
    if (element.type === 'ellipse') return (element.radiusX || 100) * 2;
    if (DUAL_RADIUS_TYPES.includes(element.type)) return (element.outerRadius || 80) * 2;
    if (element.type === 'wavy-line') return element.length || 200;
    return element.width || 0;
  };
  const getElementHeight = () => {
    if (RADIAL_TYPES.includes(element.type)) return (element.radius || 80) * 2;
    if (element.type === 'ellipse') return (element.radiusY || 60) * 2;
    if (DUAL_RADIUS_TYPES.includes(element.type)) return (element.outerRadius || 80) * 2;
    if (element.type === 'wavy-line') return element.strokeWidth || 4;
    if (element.type === 'text') return (element.fontSize || 16) * 1.5;
    return element.height || 0;
  };
  const alignToCanvas = (axis, mode) => {
    const w = getElementWidth();
    const h = getElementHeight();
    const isCentered = CENTERED_TYPES.includes(element.type);
    if (axis === 'x') {
      if (isCentered) {
        const half = w / 2;
        handleChange('x', mode === 'start' ? half : mode === 'center' ? dimensions.width / 2 : dimensions.width - half);
      } else {
        handleChange('x', mode === 'start' ? 0 : mode === 'center' ? (dimensions.width - w) / 2 : dimensions.width - w);
      }
    } else {
      if (isCentered) {
        const half = h / 2;
        handleChange('y', mode === 'start' ? half : mode === 'center' ? dimensions.height / 2 : dimensions.height - half);
      } else {
        handleChange('y', mode === 'start' ? 0 : mode === 'center' ? (dimensions.height - h) / 2 : dimensions.height - h);
      }
    }
  };

  return (
    <div className="w-64 lg:w-72 xl:w-80 bg-white border-l border-gray-200 p-4 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Properties</h3>
        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
          {element.type}
        </span>
      </div>
      
      {/* Position */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Position</h4>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-600">X</label>
            <input
              type="number"
              value={Math.round(element.x)}
              onChange={(e) => handleChange('x', parseFloat(e.target.value))}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Y</label>
            <input
              type="number"
              value={Math.round(element.y)}
              onChange={(e) => handleChange('y', parseFloat(e.target.value))}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            />
          </div>
        </div>
      </div>
      
      {/* Generic Width/Height Size */}
      {!CENTERED_TYPES.includes(element.type) && element.type !== 'wavy-line' && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Size</h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-600">Width</label>
              <input
                type="number"
                value={Math.round(element.width || 0)}
                onChange={(e) => handleChange('width', parseFloat(e.target.value))}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
            {element.type !== 'text' && (
              <div>
                <label className="text-xs text-gray-600">Height</label>
                <input
                  type="number"
                  value={Math.round(element.height || 0)}
                  onChange={(e) => handleChange('height', parseFloat(e.target.value))}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Radial Radius (circle, triangle, pentagon, hexagon, octagon, diamond, crescent) */}
      {RADIAL_TYPES.includes(element.type) && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Size</h4>
          <div>
            <label className="text-xs text-gray-600">Radius</label>
            <input
              type="number"
              value={Math.round(element.radius || 0)}
              onChange={(e) => handleChange('radius', parseFloat(e.target.value))}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            />
          </div>
        </div>
      )}

      {/* Ellipse radiusX / radiusY */}
      {element.type === 'ellipse' && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Size</h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-600">Radius X</label>
              <input type="number" value={Math.round(element.radiusX || 0)}
                onChange={(e) => handleChange('radiusX', parseFloat(e.target.value))}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"/>
            </div>
            <div>
              <label className="text-xs text-gray-600">Radius Y</label>
              <input type="number" value={Math.round(element.radiusY || 0)}
                onChange={(e) => handleChange('radiusY', parseFloat(e.target.value))}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"/>
            </div>
          </div>
        </div>
      )}

      {/* Ring / Star outerRadius + innerRadius */}
      {DUAL_RADIUS_TYPES.includes(element.type) && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Size</h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-600">Outer R</label>
              <input type="number" value={Math.round(element.outerRadius || 0)}
                onChange={(e) => handleChange('outerRadius', parseFloat(e.target.value))}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"/>
            </div>
            <div>
              <label className="text-xs text-gray-600">Inner R</label>
              <input type="number" value={Math.round(element.innerRadius || 0)}
                onChange={(e) => handleChange('innerRadius', parseFloat(e.target.value))}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"/>
            </div>
          </div>
        </div>
      )}

      {/* Wavy Line: length + amplitude */}
      {element.type === 'wavy-line' && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Size</h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-600">Length</label>
              <input type="number" value={Math.round(element.length || 200)}
                onChange={(e) => handleChange('length', parseFloat(e.target.value))}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"/>
            </div>
            <div>
              <label className="text-xs text-gray-600">Amplitude</label>
              <input type="number" value={Math.round(element.amplitude || 15)}
                onChange={(e) => handleChange('amplitude', parseFloat(e.target.value))}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"/>
            </div>
          </div>
        </div>
      )}
      
      {/* Opacity */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Opacity</h4>
        <div className="flex items-center gap-2">
          <input
            type="range"
            min="0" max="1" step="0.01"
            value={element.opacity ?? 1}
            onChange={(e) => handleChange('opacity', parseFloat(e.target.value))}
            className="flex-1"
          />
          <span className="text-xs text-gray-600 w-10 text-right">
            {Math.round((element.opacity ?? 1) * 100)}%
          </span>
        </div>
      </div>

      {/* Rotation */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Rotation</h4>
        <div className="flex items-center gap-2">
          <input
            type="range"
            min="0"
            max="360"
            value={element.rotation || 0}
            onChange={(e) => handleChange('rotation', parseFloat(e.target.value))}
            className="flex-1"
          />
          <input
            type="number"
            value={Math.round(element.rotation || 0)}
            onChange={(e) => handleChange('rotation', parseFloat(e.target.value))}
            className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
          />
          <span className="text-xs text-gray-600">°</span>
        </div>
      </div>
      
      {/* Fill Color / Line Color */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          {['line-shape', 'wavy-line'].includes(element.type) ? 'Line Color' : 'Fill Color'}
        </h4>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={
              ['line-shape', 'wavy-line'].includes(element.type) 
                ? (element.stroke || '#000000')
                : (element.fill || '#000000')
            }
            onChange={(e) => {
              const prop = ['line-shape', 'wavy-line'].includes(element.type) ? 'stroke' : 'fill';
              handleChange(prop, e.target.value);
            }}
            className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
          />
          <input
            type="text"
            value={
              ['line-shape', 'wavy-line'].includes(element.type) 
                ? (element.stroke || '#000000')
                : (element.fill || '#000000')
            }
            onChange={(e) => {
              const prop = ['line-shape', 'wavy-line'].includes(element.type) ? 'stroke' : 'fill';
              handleChange(prop, e.target.value);
            }}
            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm font-mono"
            placeholder="#000000"
          />
        </div>
      </div>
      
      {/* Stroke (for all filled shapes) */}
      {!['text', 'image', 'line-shape', 'arrow-shape', 'wavy-line', 'curved-arrow'].includes(element.type) && (
        <>
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Stroke Color</h4>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={element.stroke || '#000000'}
                onChange={(e) => handleChange('stroke', e.target.value)}
                className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={element.stroke || '#000000'}
                onChange={(e) => handleChange('stroke', e.target.value)}
                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm font-mono"
                placeholder="#000000"
              />
            </div>
          </div>
          
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Stroke Width</h4>
            <input
              type="number"
              min="0"
              value={element.strokeWidth || 0}
              onChange={(e) => handleChange('strokeWidth', parseFloat(e.target.value))}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            />
          </div>
        </>
      )}
      
      {/* Text Properties */}
      {element.type === 'text' && (
        <>
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Text Content</h4>
            <textarea
              value={element.text || ''}
              onChange={(e) => handleChange('text', e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              rows="3"
            />
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Font Size</h4>
            <input
              type="number"
              min="8"
              value={element.fontSize || 16}
              onChange={(e) => handleChange('fontSize', parseFloat(e.target.value))}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            />
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Letter Spacing</h4>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="-10"
                max="50"
                step="1"
                value={element.letterSpacing ?? 0}
                onChange={(e) => handleChange('letterSpacing', parseFloat(e.target.value))}
                className="flex-1"
              />
              <input
                type="number"
                min="-10"
                max="50"
                value={element.letterSpacing ?? 0}
                onChange={(e) => handleChange('letterSpacing', parseFloat(e.target.value))}
                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Line Height</h4>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0.5"
                max="4"
                step="0.1"
                value={element.lineHeight ?? 1}
                onChange={(e) => handleChange('lineHeight', parseFloat(e.target.value))}
                className="flex-1"
              />
              <input
                type="number"
                min="0.5"
                max="4"
                step="0.1"
                value={element.lineHeight ?? 1}
                onChange={(e) => handleChange('lineHeight', parseFloat(e.target.value))}
                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Font Family</h4>
            <div className="relative">
              <button
                onClick={() => setShowFontList(!showFontList)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-left flex items-center justify-between hover:border-blue-400 transition-colors"
                style={{ fontFamily: element.fontFamily || 'Arial' }}
              >
                <span className="truncate">{element.fontFamily || 'Arial'}</span>
                <span className="text-gray-400 text-xs ml-1">{showFontList ? '▲' : '▼'}</span>
              </button>

              {showFontList && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => { setShowFontList(false); setFontSearch(''); }}
                  />
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl">
                    <div className="p-2 border-b border-gray-100">
                      <input
                        type="text"
                        placeholder="Search fonts…"
                        value={fontSearch}
                        onChange={(e) => setFontSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:outline-none focus:border-blue-400"
                        autoFocus
                      />
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                      {ALL_FONTS
                        .filter(f => f.name.toLowerCase().includes(fontSearch.toLowerCase()))
                        .map(font => (
                          <button
                            key={font.name}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              loadGoogleFont(font.name);
                              handleChange('fontFamily', font.name);
                              setShowFontList(false);
                              setFontSearch('');
                            }}
                            className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between transition-colors ${
                              (element.fontFamily || 'Arial') === font.name
                                ? 'bg-blue-100 text-blue-800'
                                : 'hover:bg-gray-50 text-gray-700'
                            }`}
                            style={{ fontFamily: font.name }}
                          >
                            <span>{font.name}</span>
                            {font.category === 'system' && (
                              <span className="text-xs text-gray-400 font-sans ml-1">sys</span>
                            )}
                          </button>
                        ))
                      }
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Text Style: Bold / Italic / Underline + Alignment */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Text Style</h4>
            <div className="flex items-center gap-1 mb-2">
              <button
                onClick={toggleBold}
                className={`w-8 h-8 rounded font-bold text-sm border transition-colors ${
                  isBold ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
                title="Bold"
              >B</button>
              <button
                onClick={toggleItalic}
                className={`w-8 h-8 rounded italic text-sm border transition-colors ${
                  isItalic ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
                title="Italic"
              ><em>I</em></button>
              <button
                onClick={toggleUnderline}
                className={`w-8 h-8 rounded underline text-sm border transition-colors ${
                  isUnderline ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
                title="Underline"
              >U</button>
            </div>
            <div className="flex items-center gap-1">
              {[{ value: 'left', icon: <AlignLeft size={14} /> }, { value: 'center', icon: <AlignCenter size={14} /> }, { value: 'right', icon: <AlignRight size={14} /> }, { value: 'justify', icon: <AlignJustify size={14} /> }].map(({ value, icon }) => (
                <button
                  key={value}
                  onClick={() => handleChange('align', value)}
                  className={`flex-1 h-8 rounded border flex items-center justify-center transition-colors ${
                    (element.align || 'left') === value
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                  title={`Align ${value}`}
                >{icon}</button>
              ))}
            </div>
          </div>
        </>
      )}
      
      {/* Corner Radius (for rectangles) */}
      {element.type === 'rect' && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Corner Radius</h4>
          <input
            type="number"
            min="0"
            value={element.cornerRadius || 0}
            onChange={(e) => handleChange('cornerRadius', parseFloat(e.target.value))}
            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
          />
        </div>
      )}

      {/* Image Mask */}
      {element.type === 'image' && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">Mask</h4>
            {element.maskShape && (
              <button
                onClick={() => handleChange('maskShape', null)}
                className="text-xs text-red-500 hover:text-red-700 transition-colors"
              >
                Remove mask
              </button>
            )}
          </div>
          <div className="grid grid-cols-4 gap-1">
            {MASK_SHAPES.map((s) => (
              <button
                key={s.id}
                onClick={() => handleChange('maskShape', s.id)}
                title={s.label}
                className={`flex flex-col items-center gap-0.5 p-1 rounded border transition-colors ${
                  element.maskShape === s.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-transparent hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="w-10 h-10 flex items-center justify-center">{s.svg}</div>
                <span className="text-xs text-gray-500 leading-tight truncate w-full text-center">{s.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Shadow */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-700">Shadow</h4>
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={element.shadowEnabled || false}
              onChange={(e) => handleChange('shadowEnabled', e.target.checked)}
              className="rounded"
            />
            <span className="text-xs text-gray-600">Enable</span>
          </label>
        </div>
        {element.shadowEnabled && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 w-12">Color</label>
              <input type="color" value={element.shadowColor || '#000000'}
                onChange={(e) => handleChange('shadowColor', e.target.value)}
                className="w-8 h-6 border border-gray-300 rounded cursor-pointer" />
              <input type="text" value={element.shadowColor || '#000000'}
                onChange={(e) => handleChange('shadowColor', e.target.value)}
                className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs font-mono" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 w-12">Blur</label>
              <input type="range" min="0" max="50"
                value={element.shadowBlur ?? 10}
                onChange={(e) => handleChange('shadowBlur', parseFloat(e.target.value))}
                className="flex-1" />
              <span className="text-xs text-gray-600 w-6 text-right">{element.shadowBlur ?? 10}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-600">Offset X</label>
                <input type="number" value={element.shadowOffsetX ?? 5}
                  onChange={(e) => handleChange('shadowOffsetX', parseFloat(e.target.value))}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600">Offset Y</label>
                <input type="number" value={element.shadowOffsetY ?? 5}
                  onChange={(e) => handleChange('shadowOffsetY', parseFloat(e.target.value))}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 w-12">Opacity</label>
              <input type="range" min="0" max="1" step="0.05"
                value={element.shadowOpacity ?? 0.5}
                onChange={(e) => handleChange('shadowOpacity', parseFloat(e.target.value))}
                className="flex-1" />
              <span className="text-xs text-gray-600 w-8 text-right">{Math.round((element.shadowOpacity ?? 0.5) * 100)}%</span>
            </div>
          </div>
        )}
      </div>

      {/* Align to Canvas */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Align to Canvas</h4>
        <div className="space-y-1">
          <div className="flex gap-1">
            {[['⇐', 'Align Left', () => alignToCanvas('x', 'start')],
              ['↔', 'Center Horizontal', () => alignToCanvas('x', 'center')],
              ['⇒', 'Align Right', () => alignToCanvas('x', 'end')]].map(([label, title, action]) => (
              <button key={title} onClick={action} title={title}
                className="flex-1 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors">
                {label}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            {[['⇑', 'Align Top', () => alignToCanvas('y', 'start')],
              ['↕', 'Center Vertical', () => alignToCanvas('y', 'center')],
              ['⇓', 'Align Bottom', () => alignToCanvas('y', 'end')]].map(([label, title, action]) => (
              <button key={title} onClick={action} title={title}
                className="flex-1 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors">
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Element ID */}
      <div className="mt-8 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-400">ID: {element.id}</p>
      </div>
    </div>
  );
};

export default PropertiesPanel;
